import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { Platform } from 'react-native';

import { RootState } from '../../../store/models';
import { State } from '../../../store/Search.model';
import { APP } from '../../../config';
import { useIsMobile, useResponsiveEffect } from '../../../hooks';
import { toSlugCase, unSlugify } from '../../../modules/stringUtils';
import { SEARCH_PARAM_RESET_CODE } from '../../../modules/consumer/search/Constants';
import { PROVIDER_ONBOARDING_SIGNUP, SEARCH_ROUTE } from '../../../route-names';
import { LocationRecord } from '../../../store/Location.model';

import { SEARCH_SOURCE } from './constants';

import { HomeHero } from './HomeHero';
import { getWidth } from '../../../modules/Responsive';
import { listenToWidth } from '../../../modules/Responsive/listenToWidth';
import analytics from '../../../modules/analytics';
import nonCriticalException from '../../../modules/exceptionLogger';
import { AuthOrigins } from '../../../modules/user/constants';
import { useShowSubscriptionFeatures } from '../../../hooks/useShowSubscriptionFeatures';

/**
 * Renders the home page search hero.
 * @example
 * import HomeHero from 'components/consumer/HomeHero';
 *
 * const MyComponent = () => {
 *   return (
 *     <HomeHero />
 *   );
 * }
 *
 * @param query The current search query
 * @param date The search date currently set in the search criteria
 * @param latitude The latitude currently set in the search criteria
 * @param longitude The longitude currently set in the search criteria
 * @param address The address currently set in the search criteria
 * @param onPopoverOpen Called when one of the components in the home hero expands
 * to full screen
 * full screen
 * @param timeSlots
 * @param utmParams
 * @param linkTo
 */
export function HomeHeroContainer({
  query,
  date,
  latitude,
  longitude,
  placeId,
  address,
  timeSlots,
  utmParams,
  linkTo,
  locationCreated,
}) {
  const addressLocation = useSelector<RootState, State['lastSearchLocation']>(state => state.search.lastSearchLocation);
  const [canShowSubscriptionFeatures] = useShowSubscriptionFeatures();

  const breakpoints = useResponsiveEffect();
  const [width, setWidth] = useState(getWidth);
  const locationRef = useRef(null);
  const [searchParams, setSearchParams] = useState({ q: null });
  const isMobile = useIsMobile();
  const [location, setLocation] = useState({
    address,
    latitude,
    longitude,
    placeId,
    created: locationCreated,
  });

  useEffect(() => {
    setLocation({
      ...location,
      ...addressLocation,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressLocation]);

  const linkParams = useMemo(() => {
    const { q } = searchParams;
    return {
      q: toSlugCase(q || query),
      date,
      loc: toSlugCase(location?.address),
      lat: location.latitude ?? SEARCH_PARAM_RESET_CODE,
      lon: location.longitude ?? SEARCH_PARAM_RESET_CODE,
      timeSlots,
      source: SEARCH_SOURCE,
      placeId: location?.placeId ?? SEARCH_PARAM_RESET_CODE,
      ...utmParams,
    };
  }, [
    searchParams,
    query,
    date,
    location?.address,
    location.latitude,
    location.longitude,
    location?.placeId,
    timeSlots,
    utmParams,
  ]);

  const handleShortSubmitClick = () => linkTo(Platform.OS === 'web' ? SEARCH_ROUTE : null, linkParams);

  /**
   * Updates the Search Query
   * if "doExecuteSearch" is true then the user is taken to the search page
   * @returns {void|any}
   * @param qry
   * @param canSubmitQuery
   */
  const updateQuery = (qry, canSubmitQuery = true) => {
    const newSearchParams = {
      ...linkParams,
      q: qry,
    };

    if (canSubmitQuery && location?.address) {
      return linkTo(SEARCH_ROUTE, newSearchParams);
    }

    return setSearchParams({ ...newSearchParams });
  };

  const handleSearchPopoverClose = qry => {
    updateQuery(qry, false);
  };

  const handleQueryBlur = () => {
    if (locationRef.current && !location?.address) {
      locationRef.current.input.focus();
    }
  };

  const goToJoinPage = async () => {
    try {
      await analytics.track('pro_hero_block_join_page_clicked', {
        platform: Platform.OS,
      });
    } catch (e) {
      nonCriticalException(e);
    }

    linkTo(PROVIDER_ONBOARDING_SIGNUP, {
      source: AuthOrigins.HeaderNav,
    });
  };

  const { q } = searchParams;
  const displayQ = unSlugify(q);
  const mobileLayout = APP || isMobile;
  const showSearchPopoverTrigger = (mobileLayout || breakpoints.tablet) && width <= 535;

  const updateLocation = (locationRecord: LocationRecord) => {
    setLocation({
      address: locationRecord.address,
      latitude: locationRecord.latitude,
      longitude: locationRecord.longitude,
      placeId: locationRecord.placeId,
      created: location.created,
    });
  };

  const isIpadWidthOrLess = width <= 1206;

  useEffect(() => {
    const handleResize = newHeight => setWidth(newHeight);
    return listenToWidth(handleResize);
  });

  return (
    <HomeHero
      showSearchPopoverTrigger={showSearchPopoverTrigger}
      updateQuery={updateQuery}
      mobileLayout={mobileLayout}
      canShowSubscriptionFeatures={canShowSubscriptionFeatures}
      goToJoinPage={goToJoinPage}
      displayQ={displayQ}
      locationRef={locationRef}
      location={location}
      updateLocation={updateLocation}
      handleSearchPopoverClose={handleSearchPopoverClose}
      handleShortSubmitClick={handleShortSubmitClick}
      handleQueryBlur={handleQueryBlur}
      breakpoints={breakpoints}
      isIpadWidthOrLess={isIpadWidthOrLess}
    />
  );
}

HomeHeroContainer.propTypes = {
  latitude: PropTypes.number,
  longitude: PropTypes.number,
  placeId: PropTypes.string,
  query: PropTypes.string,
  date: PropTypes.string,
  timeSlots: PropTypes.any,
  address: PropTypes.string,
  utmParams: PropTypes.object,
  linkTo: PropTypes.func.isRequired,
  /** The timestamp of when the geolocation was stored */
  locationCreated: PropTypes.number,
};

HomeHeroContainer.defaultProps = {
  query: null,
  date: null,
  latitude: null,
  longitude: null,
  placeId: null,
  address: null,
  timeSlots: [],
  utmParams: {},
  locationCreated: undefined,
};

const mapStateToProps = ({
  search,
  location,
  route,
}) => {
  let locationFrom = search;
  if (!!locationFrom.address === false) {
    locationFrom = location;
    if (!!locationFrom.address === false) {
      locationFrom = search.lastSearchLocation;
    }
  }

  return {
    date: search.date,
    query: search.query,
    address: locationFrom.address,
    // checking address here rather than lat / lon so that we don't run into any
    // issues where the lat / lon are set but the address isn't
    latitude: locationFrom.latitude,
    longitude: locationFrom.longitude,
    placeId: locationFrom?.placeId,
    timeSlots: locationFrom?.timeSlots,
    locationCreated: !locationFrom.address ? locationFrom?.created : undefined,
    utmParams: route.params,
  };
};

const mapDispatchToProps = dispatch => ({
  linkTo: (route, params) => dispatch.route.go({
    route,
    params,
    notify: true,
  }),
});

export default connect(mapStateToProps, mapDispatchToProps)(HomeHeroContainer);
